package com.swetdot.swetgram.ui.picso

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import coil3.compose.rememberAsyncImagePainter
import coil3.compose.AsyncImagePainter
import coil3.compose.LocalPlatformContext
import coil3.request.CachePolicy
import coil3.request.ImageRequest
import com.swetdot.swetgram.domain.PicsoPostItem
import com.swetdot.swetgram.domain.isActiveAt
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.*
import kotlin.time.Instant

/** Authenticated bytes belong only to this visible composition; neither cache writes nor URL fetches. */
@Composable
internal fun PicsoImage(
    post: PicsoPostItem,
    loadImage: suspend (String) -> ByteArray,
    now: () -> Instant,
    modifier: Modifier = Modifier,
    contentScale: ContentScale = ContentScale.Crop,
) {
    var bytes by remember(post.id) { mutableStateOf<ByteArray?>(null) }
    var failed by remember(post.id) { mutableStateOf(false) }
    var retry by remember(post.id) { mutableIntStateOf(0) }
    var expired by remember(post.id) { mutableStateOf(!post.isActiveAt(now())) }
    LaunchedEffect(post.id, post.expiresAt) {
        while (post.isActiveAt(now())) delay(250)
        bytes = null
        expired = true
    }
    LaunchedEffect(post.id, retry) {
        bytes = null
        failed = false
        try {
            if (post.isActiveAt(now())) {
                val loaded = loadImage(post.id.value)
                if (post.isActiveAt(now())) bytes = loaded
            }
        } catch (c: CancellationException) { throw c }
        catch (_: Exception) { failed = true }
    }
    Box(modifier, contentAlignment = Alignment.Center) {
        val data = bytes
        if (expired) {
            Text(stringResource(Res.string.picso_error_expired), color = Color.White)
        } else if (data != null && post.isActiveAt(now())) {
            val context = LocalPlatformContext.current
            val request = remember(data) {
                ImageRequest.Builder(context).data(data)
                    .memoryCachePolicy(CachePolicy.DISABLED)
                    .diskCachePolicy(CachePolicy.DISABLED).build()
            }
            val painter = rememberAsyncImagePainter(request, contentScale = contentScale)
            val imageState by painter.state.collectAsState()
            if (imageState is AsyncImagePainter.State.Error) {
                TextButton(onClick = { retry++ }) { Text(stringResource(Res.string.picso_retry), color = Color.White) }
            } else Image(
                painter = painter, contentDescription = post.caption ?: "@${post.authorUsername}",
                contentScale = contentScale, modifier = Modifier.fillMaxSize(),
            )
        } else if (failed) {
            TextButton(onClick = { retry++ }) { Text(stringResource(Res.string.picso_retry), color = Color.White) }
        } else {
            CircularProgressIndicator(color = Color.White)
        }
    }
}
